import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { cn } from '@/lib/core/utils/cn';
import { type HeroSlide } from '@/lib/core/utils';
import { useShopHeroSlides } from '@/services/platform/data/shop';

import { ScreenHead } from '@/components/shared/screen/head';
import { ShopHeaderActions } from '@/components/shared/shop/layout/ShopHeaderActions';
import { ShopSectionContentResponseModel } from '@/services/platform/api/model';
import type { ShopSection } from '@/services/platform/data/shop/types';
import type { AppContext } from '@/lib/core/types/modules';

import { ShopSectionBlockDispatcher } from './blocks';

// ── Types ─────────────────────────────────────────────────────────────────────

export type { ShopSection };

export interface ShopSectionIntroScreenProps {
  /** CMS page content — used for the hero slideshow once backend wires up. */
  content?: ShopSectionContentResponseModel;
  /**
   * Section slug resolved against the CMS `shopSectionsRepository` by
   * `getSectionFromPath`. Empty when no section was matched.
   */
  section: ShopSection;
  context?: AppContext;
}

// ── Section config ────────────────────────────────────────────────────────────

interface SectionConfig {
  titleKey: TranslationKey;
  showFollowBrands: boolean;
}

const DEFAULT_SECTION_CONFIG: SectionConfig = {
  titleKey: 'App.Shop.Women',
  showFollowBrands: true,
};

/*
 * TODO: move titleKey and showFollowBrands into the CMS Section content type
 * so adding a new section in Umbraco no longer requires a code change.
 */
const sectionConfigs: Record<string, SectionConfig> = {
  women: { titleKey: 'App.Shop.Women', showFollowBrands: true },
  men: { titleKey: 'App.Shop.Men', showFollowBrands: true },
  home: { titleKey: 'App.Shop.Home', showFollowBrands: true },
  fragrances: { titleKey: 'App.Shop.Fragrances', showFollowBrands: false },
};

// ── Stub hero slides ──────────────────────────────────────────────────────────

function getStubHeroSlides(): HeroSlide[] {
  // Fallback used until the CMS heroSlideshow block list is populated.
  return [{ id: 'hero-1' }, { id: 'hero-2' }, { id: 'hero-3' }];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShopSectionIntroScreen({ content, section, context }: ShopSectionIntroScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);

  /*
   * CMS section slugs are prefixed (e.g. `gender_women`) while the config map
   * keys are plain (`women`) — normalise to the last segment for lookups.
   */
  const sectionKey = section.split('_').pop() ?? section;
  const config = sectionConfigs[sectionKey] ?? DEFAULT_SECTION_CONFIG;
  // Header title comes from the CMS page; the config key is only a fallback.
  const headerTitle = content?.properties?.pageName ?? content?.name ?? t(config.titleKey);
  const [heroIndex, setHeroIndex] = useState(0);

  const cmsHeroSlides = useShopHeroSlides(content?.properties?.heroSlideshow?.items);
  const heroSlides = cmsHeroSlides.length > 0 ? cmsHeroSlides : getStubHeroSlides();
  const cmsBlocks = content?.properties?.components?.items ?? [];

  return (
    <div className="relative flex flex-col bg-white">
      {/* ── Hero carousel ─────────────────────────────────────────────────── */}
      <div className="relative h-156 shrink-0">
        {/*
         * Header overlaid on hero — gradient ensures legibility.
         * bg-gradient-to-b: from opaque black at top, fades to transparent going down.
         */}
        <div className="pt-safe-area fixed top-0 right-0 left-0 z-80 bg-linear-to-b from-black/80 to-transparent pb-4 backdrop-blur-[2.5px]">
          <ScreenHead
            title={headerTitle}
            onBack={handleBack}
            ariaLabel={t('App.Labels.GoBack')}
            variant="overlay"
            className="pt-0"
            rightActions={<ShopHeaderActions context={context} showHeroCarousel={true} />}
          />
        </div>

        {/* Hero slides */}
        <Swiper
          className="h-full"
          slidesPerView={1}
          onActiveIndexChange={swiper => setHeroIndex(swiper.activeIndex)}
        >
          {heroSlides.map(slide => {
            const link = slide.link;
            const slideContent = (
              <div
                className="bg-jncquoi-neutral-1000 relative h-full w-full"
                style={
                  slide.image
                    ? {
                        backgroundImage: `url(${slide.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : undefined
                }
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.4) 79%)',
                  }}
                />
              </div>
            );

            return (
              <SwiperSlide key={slide.id}>
                {link?.href ? (
                  <button
                    type="button"
                    className="block h-full w-full cursor-pointer text-left"
                    onClick={() => (link.newTab ? window.open(link.href, '_blank') : navigate(link.href))}
                  >
                    {slideContent}
                  </button>
                ) : (
                  slideContent
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Text + pagination overlay */}
        <div className="pointer-events-none absolute right-4 bottom-10 left-4 z-10 flex flex-col gap-8">
          {(heroSlides[heroIndex]?.eyebrow || heroSlides[heroIndex]?.headline) && (
            <div className="flex flex-col gap-6">
              {heroSlides[heroIndex].eyebrow && (
                <p className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-white uppercase">
                  {heroSlides[heroIndex].eyebrow}
                </p>
              )}
              {heroSlides[heroIndex].headline && (
                <p className="font-pp-neue-montreal text-[28px] leading-tight font-semibold tracking-[0.5px] whitespace-pre-line text-white uppercase">
                  {heroSlides[heroIndex].headline}
                </p>
              )}
            </div>
          )}
          {heroSlides.length > 1 && (
            <div className="flex items-center gap-2" aria-hidden="true">
              {heroSlides.map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    'block h-px w-4 transition-all duration-300',
                    i === heroIndex ? 'bg-white' : 'bg-white/40'
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Scrollable content sections ───────────────────────────────────── */}
      {/*
       * pb clears the floating AppNavBar pill (fixed at `bottom: 1.5rem +
       * safe-area-inset-bottom`, `h-12` tall — see components/layout/navBar/index.tsx)
       * plus a small gap, so the last section's CTA (e.g. "See all subcategories")
       * doesn't render underneath it. A flat `pb-25` (no safe-area term) undershoots
       * on devices with a tall safe-area-inset-bottom, where the pill sits higher.
       */}
      <div className="flex flex-col gap-18 pt-12 pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
        {/*
         * CMS-driven blocks come first. The Umbraco editor decides which blocks
         * appear and in what order via the Shop Section's `components` block list.
         * The legacy stub-driven blocks below act as a visual fallback for blocks
         * that the backend has not yet modelled (Brands, Editorials, Trending,
         * Subcategory, FamilyScent). They will be retired one by one as the
         * backend exposes equivalent CMS blocks.
         */}
        {cmsBlocks.map((block, index) => (
          <ShopSectionBlockDispatcher
            key={index}
            block={block}
            section={section}
            sectionId={content?.properties?.sectionPicker?.[0]?.id}
          />
        ))}
      </div>
    </div>
  );
}
